import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import defaultTourData from './tourSteps.json';
import './OnboardingTourModal.css';

const DEFAULT_WELCOME_SCREEN = {
  title: 'Welcome to AI Image Generator! 🎨',
  subtitle: 'Watch our tutorial video to learn how to create stunning AI-generated images in just a few steps',
  note: '',
  primaryButtonText: 'Start Tour',
  secondaryButtonText: 'Get Started',
  videoTitle: 'Tutorial Video',
  variant: 'default',
  showVideo: true,
  showCloseButton: true,
  showSecondaryButton: true,
};

const DEFAULT_LABELS = {
  step: 'Step',
  of: 'of',
  previous: 'Previous',
  next: 'Next',
  finish: 'Finish Tour',
};

const FALLBACK_TOUR_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="700" height="400" viewBox="0 0 700 400"%3E%3Crect fill="%23f0f2f5" width="700" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="24" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ETour Step%3C/text%3E%3C/svg%3E';
const FALLBACK_VIDEO_THUMBNAIL = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360"%3E%3Crect fill="%23f5f5f5" width="640" height="360"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="30" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EVideo Preview%3C/text%3E%3C/svg%3E';

const normalizeTourData = (tourData = {}) => {
  const welcomeScreen = tourData.welcomeScreen || {};
  const welcomeVideo = welcomeScreen.video || tourData.welcomeVideo || {};
  const tourSteps = Array.isArray(tourData.tourSteps) ? tourData.tourSteps : [];

  return {
    welcomeScreen: {
      ...DEFAULT_WELCOME_SCREEN,
      ...welcomeScreen,
      showVideo: typeof welcomeScreen.showVideo === 'boolean'
        ? welcomeScreen.showVideo
        : Boolean(welcomeVideo.url || welcomeVideo.thumbnail),
      showCloseButton: typeof welcomeScreen.showCloseButton === 'boolean'
        ? welcomeScreen.showCloseButton
        : DEFAULT_WELCOME_SCREEN.showCloseButton,
      showSecondaryButton: typeof welcomeScreen.showSecondaryButton === 'boolean'
        ? welcomeScreen.showSecondaryButton
        : DEFAULT_WELCOME_SCREEN.showSecondaryButton,
      video: {
        url: welcomeVideo.url || '',
        thumbnail: welcomeVideo.thumbnail || '',
        title: welcomeVideo.title || welcomeScreen.videoTitle || DEFAULT_WELCOME_SCREEN.videoTitle,
      },
    },
    labels: {
      ...DEFAULT_LABELS,
      ...(tourData.labels || {}),
    },
    settings: {
      welcomeWidth: tourData.settings?.welcomeWidth || tourData.welcomeWidth || 900,
      tourWidth: tourData.settings?.tourWidth || tourData.tourWidth || 800,
      showWelcomeScreen: typeof tourData.settings?.showWelcomeScreen === 'boolean'
        ? tourData.settings.showWelcomeScreen
        : typeof tourData.showWelcomeScreen === 'boolean'
          ? tourData.showWelcomeScreen
          : true,
    },
    tourSteps,
  };
};

// Helper function to extract YouTube video ID and create embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url) return '';
  
  // Extract video ID from various YouTube URL formats
  let videoId = '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      videoId = match[1];
      break;
    }
  }
  
  if (!videoId) return ''; // Return empty if not a YouTube URL
  
  // Return embed URL without autoplay
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`;
};

const OnboardingTourModal = ({ visible, onClose, onSkip, tourData = defaultTourData }) => {
  const [currentView, setCurrentView] = useState('welcome'); // 'welcome' or 'tour'
  const [currentStep, setCurrentStep] = useState(0);
  const normalizedTourData = useMemo(() => normalizeTourData(tourData), [tourData]);
  const { welcomeScreen, labels, settings, tourSteps } = normalizedTourData;
  const totalSteps = tourSteps.length;
  const hasSteps = totalSteps > 0;
  const hasWelcomeScreen = settings.showWelcomeScreen;
  const embeddedVideoUrl = getYouTubeEmbedUrl(welcomeScreen.video.url);
  const shouldShowWelcomeVideo = welcomeScreen.showVideo && (embeddedVideoUrl || welcomeScreen.video.thumbnail);

  useEffect(() => {
    if (visible) {
      setCurrentView(hasWelcomeScreen ? 'welcome' : 'tour');
      setCurrentStep(0);
    }
  }, [visible, hasWelcomeScreen, normalizedTourData]);

  useEffect(() => {
    if (!visible) return;
    tourSteps.forEach((step) => {
      if (step.image) {
        const img = new Image();
        img.src = step.image;
      }
    });
  }, [visible, tourSteps]);

  const handleStartTour = () => {
    if (hasSteps) {
      setCurrentView('tour');
      return;
    }

    onClose();
  };

  const handleGetStarted = () => {
    onSkip();
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDotClick = (index) => {
    setCurrentStep(index);
  };

  const handleFinish = () => {
    onClose();
  };

  const handleKeyPress = (e) => {
    if (currentView === 'tour') {
      if (e.key === 'ArrowRight' && currentStep < totalSteps - 1) {
        handleNext();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        handlePrev();
      } else if (e.key === 'Escape') {
        onSkip();
      }
    }
  };

  useEffect(() => {
    if (visible && currentView === 'tour') {
      window.addEventListener('keydown', handleKeyPress);
      return () => {
        window.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [visible, currentView, currentStep]);

  const currentStepData = tourSteps[currentStep];

  return (
    <Modal
      visible={visible}
      onCancel={onSkip}
      footer={null}
      width={currentView === 'welcome' ? settings.welcomeWidth : settings.tourWidth}
      centered
      destroyOnClose={true}
      className="onboarding-tour-modal"
      closable={false}
    >
      {currentView === 'welcome' ? (
        <div className={`onboarding-welcome-screen ${welcomeScreen.variant === 'simple' ? 'onboarding-welcome-screen-simple' : ''}`}>
          {welcomeScreen.showCloseButton && (
            <div className="welcome-close-btn" onClick={onSkip}>
              <CloseOutlined style={{ fontSize: '16px', color: '#8C8C8C' }} />
            </div>
          )}
          
          <h2 className="welcome-title manrope">{welcomeScreen.title}</h2>
          <p className="welcome-subtitle manrope">{welcomeScreen.subtitle}</p>

          {shouldShowWelcomeVideo && (
            <div className={`welcome-video-container ${!embeddedVideoUrl ? 'has-thumbnail' : ''}`}>
              {embeddedVideoUrl ? (
                <iframe
                  src={embeddedVideoUrl}
                  title={welcomeScreen.video.title}
                  className="welcome-video-embed"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <>
                  <img 
                    src={welcomeScreen.video.thumbnail || FALLBACK_VIDEO_THUMBNAIL}
                    alt={welcomeScreen.video.title}
                    className="welcome-video-thumbnail"
                    onError={(e) => {
                      console.error('Failed to load video thumbnail:', welcomeScreen.video.thumbnail);
                      e.target.onerror = null;
                      e.target.src = FALLBACK_VIDEO_THUMBNAIL;
                    }}
                  />
                  <div 
                    className="video-play-overlay"
                    onClick={() => {
                      if (welcomeScreen.video.url) {
                        window.open(welcomeScreen.video.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <div className="play-icon">
                      <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                        <circle cx="32" cy="32" r="32" fill="white" fillOpacity="0.95"/>
                        <path d="M26 20L44 32L26 44V20Z" fill="#1890ff"/>
                      </svg>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className={`welcome-actions ${!welcomeScreen.showSecondaryButton ? 'single-action' : ''}`}>
            {welcomeScreen.showSecondaryButton && (
              <Button 
                className="welcome-btn-secondary manrope"
                size="large"
                onClick={handleGetStarted}
              >
                {welcomeScreen.secondaryButtonText}
              </Button>
            )}
            <Button 
              className="welcome-btn-primary manrope"
              type="primary"
              size="large"
              onClick={handleStartTour}
              disabled={!hasSteps}
            >
              {hasSteps ? welcomeScreen.primaryButtonText : 'No Tour Steps Available'}
            </Button>
          </div>

          {welcomeScreen.note ? <p className="welcome-note manrope">{welcomeScreen.note}</p> : null}
        </div>
      ) : (
        <div className="onboarding-tour-carousel">
          <div className="tour-close-btn" onClick={onSkip}>
            <CloseOutlined style={{ fontSize: '16px', color: '#8C8C8C' }} />
          </div>

          {currentStepData ? (
            <>
              <div className="tour-content">
                <div className="tour-image-container">
                  <img 
                    src={currentStepData.image || FALLBACK_TOUR_IMAGE}
                    alt={currentStepData.title}
                    className="tour-screenshot"
                    onError={(e) => {
                      console.error('Failed to load image:', currentStepData.image);
                      console.error('Current step:', currentStep, 'Step data:', currentStepData);
                      e.target.onerror = null;
                      e.target.src = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="700" height="400" viewBox="0 0 700 400"%3E%3Crect fill="%23f0f2f5" width="700" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="24" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3E${encodeURIComponent(currentStepData.title || `Step ${currentStepData.id || currentStep + 1}`)}%3C/text%3E%3C/svg%3E`;
                    }}
                  />
                  {currentStepData.highlights && currentStepData.highlights.map((highlight, index) => (
                    <div
                      key={index}
                      className={`tour-highlight tour-highlight-${highlight.type}`}
                      style={{
                        left: highlight.x,
                        top: highlight.y,
                        width: highlight.size,
                        height: highlight.size,
                        '--highlight-color': highlight.color || '#3b82f6',
                      }}
                    />
                  ))}
                </div>

                <div className="tour-text-content">
                  <div className="tour-step-indicator manrope">
                    {labels.step} {currentStep + 1} {labels.of} {totalSteps}
                  </div>
                  <h3 className="tour-step-title manrope">{currentStepData.title}</h3>
                  <p className="tour-step-description manrope">{currentStepData.description}</p>
                </div>
              </div>

              <div className="tour-navigation">
                <div className="tour-progress-dots">
                  {tourSteps.map((step, index) => (
                    <div
                      key={step.id || index}
                      className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                      onClick={() => handleDotClick(index)}
                    />
                  ))}
                </div>

                <div className="tour-nav-buttons">
                  <Button
                    className="tour-nav-btn manrope"
                    onClick={handlePrev}
                    disabled={currentStep === 0}
                    icon={<LeftOutlined />}
                  >
                    {labels.previous}
                  </Button>

                  {currentStep === totalSteps - 1 ? (
                    <Button
                      className="tour-nav-btn-primary manrope"
                      type="primary"
                      onClick={handleFinish}
                    >
                      {labels.finish}
                    </Button>
                  ) : (
                    <Button
                      className="tour-nav-btn-primary manrope"
                      type="primary"
                      onClick={handleNext}
                    >
                      {labels.next}
                      <RightOutlined />
                    </Button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="tour-content">
              <div className="tour-text-content" style={{ width: '100%', textAlign: 'center' }}>
                <h3 className="tour-step-title manrope">No tour steps configured</h3>
                <p className="tour-step-description manrope">
                  Add items to the JSON file under <strong>tourSteps</strong> to use this tour.
                </p>
                <div className="tour-nav-buttons" style={{ justifyContent: 'center', marginTop: 24 }}>
                  <Button
                    className="tour-nav-btn-primary manrope"
                    type="primary"
                    onClick={handleFinish}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default OnboardingTourModal;
